/**
 * "Delete your account?" dialog — reason, optional detail, and a typed DELETE
 * confirmation.
 *
 * Copy, field order, and the confirmation rule (case-insensitive, trimmed) are
 * verbatim. In backend mode this now deletes the account immediately; in mock mode the
 * authApi still only simulates the request.
 */
import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import ConfirmDialog from './ConfirmDialog';
import { Select, TextField } from '../forms';
import { DELETE_REASONS } from '../../data/faq';
import { c } from '../../styles/theme';

export interface DeleteAccountModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (input: { reason: string; detail: string }) => void;
}

export default function DeleteAccountModal({ open, onClose, onConfirm }: DeleteAccountModalProps) {
  const [reason, setReason] = useState('');
  const [detail, setDetail] = useState('');
  const [confirmText, setConfirmText] = useState('');

  const reset = () => {
    setReason('');
    setDetail('');
    setConfirmText('');
  };

  const close = () => {
    reset();
    onClose();
  };

  return (
    <ConfirmDialog
      open={open}
      onClose={close}
      title="Delete your account?"
      icon={<Trash2 size={19} color={c.danger} />}
      destructive
      showClose
      confirmLabel="Delete my account"
      confirmIcon={<Trash2 size={13} />}
      confirmDisabled={!reason || confirmText.trim().toUpperCase() !== 'DELETE'}
      onConfirm={() => {
        onConfirm({ reason, detail });
        reset();
      }}
    >
      <p style={{ fontSize: 12, color: c.text2, margin: '0 0 16px', lineHeight: 1.5 }}>
        This permanently deletes your account and all associated data, including your songs,
        purchases, receipts, and credit balances. Audio files become inaccessible immediately and
        are permanently deleted after 60 days. This action cannot be undone, your account cannot be
        restored, and outside the restricted administrative archive it will be treated as though it
        never existed.
      </p>

      <Select
        id="lyx-delete-reason"
        label="Why are you leaving?"
        value={reason}
        onChange={setReason}
        options={DELETE_REASONS}
        placeholder="Select a reason"
        marginBottom={10}
        style={{ padding: '8px 10px' }}
      />

      <TextField
        multiline
        rows={2}
        value={detail}
        onChange={setDetail}
        placeholder="Anything else you'd like us to know? (optional)"
        ariaLabel="Additional feedback"
        marginBottom={14}
      />

      <label
        htmlFor="lyx-delete-confirm"
        style={{ fontSize: 11, color: c.text2, display: 'block', margin: '0 0 6px' }}
      >
        Type <span style={{ color: c.text1, fontFamily: 'IBM Plex Mono' }}>DELETE</span> to confirm
      </label>
      <TextField
        id="lyx-delete-confirm"
        value={confirmText}
        onChange={setConfirmText}
        placeholder="DELETE"
        mono
        marginBottom={16}
        style={{ padding: '8px 10px' }}
      />
    </ConfirmDialog>
  );
}
